// cSpell: ignore zolpidem eszopiclone zaleplon trazodone mirtazapine doxepin
// cSpell: ignore benzodiazepine suvorexant lemborexant diphenhydramine doxylamine
// cSpell: ignore quetiapine Seroquel olanzapine Zyprexa risperidone Risperdal
// cSpell: ignore aripiprazole Abilify lurasidone Latuda valproate Depakote lamotrigine Lamictal Lithobid

import React, {useEffect, useState} from 'react';
import ButtonSolid from 'components/Buttons/ButtonSolid';
import {submitSleepioIntake, updatePatientAddressWeb, uploadInsuranceCardWeb} from 'externalAPIs/insurance-eligibility';
import {InsuranceCardUpload} from 'surveys/insomnia_funnel/appointment/components/InsuranceCardUpload';
import {US_STATES} from 'surveys/insomnia_funnel/survey/config';
import {surveyInstance} from 'screens/InsomniaSurvey/InsomniaSurvey';
import {getIsHighOsaRisk} from 'screens/InsomniaSurvey/utils/apneaScoring';
import {getPayerIdByValue} from 'surveys/insomnia_funnel/paywall/utils';
import Analytics from 'utils/Analytics';
import {assertAdultDob, buildIsiSnapshot, buildOsaRisk, convertDateToISODate, normalizePhone} from 'sleepioRx/helpers';
import {
  NewAsyncPatientRequest,
  SLEEPIO_CONSENT_DOC_VERSION,
  SLEEPIO_CONSENT_VERSION,
  SLEEPIO_DISCLOSURE_VERSION,
  SleepioConcurrentTreatmentOption,
  SleepioConditionStability,
  SleepioMedications,
  SleepioMentalHealthScreen,
  SleepioOsaDiagnosed,
  SleepioOsaTreatment,
  SleepioPriorDiagnosis,
  SleepioSleepDifficultyHistory,
} from 'sleepioRx/types';
import {SurveyData} from 'screens/InsomniaSurvey/utils/surveyData';
import styles from './SleepioRxInterstitials.module.css';

type IntakeStep =
  | 'duration'
  | 'frequency'
  | 'rx'
  | 'otc'
  | 'psych'
  | 'concurrent'
  | 'osaDiagnosed'
  | 'osaTreatment'
  | 'priorDiagnosis'
  | 'conditionStability'
  | 'indication'
  | 'insuranceCard'
  | 'address'
  | 'confirmation'
  | 'final';

type Option<T extends string> = {label: string; value: T};

type SleepioIntakeInterstitialProps = {
  initialStep?: IntakeStep;
};

const DURATION_OPTIONS: Option<SleepioSleepDifficultyHistory['duration']>[] = [
  {label: 'Less than 1 month', value: 'less_than_1_month'},
  {label: '1-3 months', value: '1_to_3_months'},
  {label: '3-6 months', value: '3_to_6_months'},
  {label: '6-12 months', value: '6_to_12_months'},
  {label: 'More than 1 year', value: 'more_than_1_year'},
];

const FREQUENCY_OPTIONS: Option<SleepioSleepDifficultyHistory['frequency']>[] = [
  {label: 'Less than 1 night per week', value: 'less_than_1_per_week'},
  {label: '1-2 nights per week', value: '1_to_2_per_week'},
  {label: '3-4 nights per week', value: '3_to_4_per_week'},
  {label: '5-6 nights per week', value: '5_to_6_per_week'},
  {label: 'Every night', value: 'every_night'},
];

// Enriched 2026-06 to mirror nyx PostPaywallSleepioMedicationsScreen (Quetiapine
// added — the common off-label-for-sleep antipsychotic patients report here).
const RX_OPTIONS: Option<string>[] = [
  {label: 'None', value: 'none'},
  {label: 'Zolpidem (Ambien)', value: 'zolpidem'},
  {label: 'Eszopiclone (Lunesta)', value: 'eszopiclone'},
  {label: 'Zaleplon (Sonata)', value: 'zaleplon'},
  {label: 'Trazodone', value: 'trazodone'},
  {label: 'Mirtazapine', value: 'mirtazapine'},
  {label: 'Doxepin (Silenor)', value: 'doxepin'},
  {label: 'Quetiapine (Seroquel)', value: 'quetiapine_seroquel'},
  {label: 'Benzodiazepine (Xanax, Ativan, etc.)', value: 'benzodiazepine'},
  {label: 'Suvorexant / Lemborexant', value: 'suvorexant_lemborexant'},
  {label: 'Other Rx sleep med', value: 'other_rx'},
];

const OTC_OPTIONS: Option<string>[] = [
  {label: 'None', value: 'none'},
  {label: 'Diphenhydramine (Benadryl, ZzzQuil, Tylenol PM, etc.)', value: 'diphenhydramine'},
  {label: 'Melatonin', value: 'melatonin'},
  {label: 'Doxylamine (Unisom)', value: 'doxylamine'},
  {label: 'Magnesium', value: 'magnesium'},
  {label: 'Other herbal / OTC', value: 'other_otc'},
];

// Enriched 2026-06 to mirror nyx — antipsychotics + mood stabilizers named
// brand-first so recognition doesn't depend on the patient knowing the drug class.
const PSYCH_MED_OPTIONS: Option<string>[] = [
  {label: 'None', value: 'none'},
  {label: 'ADHD med / stimulant', value: 'stimulant'},
  {label: 'Antidepressant (SSRI, SNRI)', value: 'antidepressant'},
  {label: 'Anti-anxiety medication', value: 'anxiolytic'},
  {label: 'Lithium (Lithobid)', value: 'lithium'},
  {label: 'Depakote (valproate)', value: 'valproate'},
  {label: 'Lamictal (lamotrigine)', value: 'lamotrigine'},
  {label: 'Other mood stabilizer', value: 'mood_stabilizer'},
  {label: 'Seroquel (quetiapine)', value: 'quetiapine'},
  {label: 'Abilify (aripiprazole)', value: 'aripiprazole'},
  {label: 'Risperdal (risperidone)', value: 'risperidone'},
  {label: 'Zyprexa (olanzapine)', value: 'olanzapine'},
  {label: 'Latuda (lurasidone)', value: 'lurasidone'},
  {label: 'Other antipsychotic', value: 'antipsychotic'},
  {label: 'Other psychiatric med', value: 'other_psych'},
];

const CONCURRENT_OPTIONS: Option<SleepioConcurrentTreatmentOption>[] = [
  {label: 'Therapist or counselor (talk therapy)', value: 'therapist'},
  {label: 'Psychiatrist (medication management)', value: 'psychiatrist'},
  {label: 'Neither', value: 'neither'},
  {label: 'Prefer not to say', value: 'prefer_not_to_say'},
];

// NEW 2026-06 — prior-diagnosis screen (mirrors nyx PostPaywallSleepioPriorDiagnosisScreen).
// "None of these" is pinned to the TOP and is exclusive. Copy verbatim from nyx.
const PRIOR_DIAGNOSIS_OPTIONS: Option<SleepioPriorDiagnosis>[] = [
  {label: 'None of these', value: 'none'},
  {label: 'Bipolar disorder (sometimes called manic depression)', value: 'bipolar'},
  {label: 'Schizophrenia or another psychotic-spectrum disorder', value: 'psychotic_spectrum'},
  {label: 'Seizure disorder or epilepsy', value: 'seizure_epilepsy'},
  {label: "A neurodegenerative condition (e.g., Parkinson's disease, multiple sclerosis)", value: 'neurodegenerative'},
  {label: 'Attention-deficit/hyperactivity disorder (ADHD)', value: 'adhd'},
  {label: 'Prefer not to say', value: 'prefer_not_to_say'},
];

// Diagnoses that trigger the conditional stability follow-up. ADHD is context-only.
const FLAGGED_DIAGNOSES: SleepioPriorDiagnosis[] = [
  'bipolar',
  'psychotic_spectrum',
  'seizure_epilepsy',
  'neurodegenerative',
];

// Exclusive "no affirmative" prior-diagnosis options.
const PRIOR_DIAGNOSIS_EXCLUSIVE: SleepioPriorDiagnosis[] = ['none', 'prefer_not_to_say'];

const CONDITION_STABILITY_OPTIONS: Option<SleepioConditionStability>[] = [
  {label: "Yes — it's stable and managed", value: 'stable_managed'},
  {label: "No, or I'm not sure", value: 'not_stable_or_unsure'},
];

// OSA follow-up — shown only to high-OSA-risk users (Australia score ≥ 10), mirrors
// nyx PostPaywallSleepioOsaFollowUpScreen. Informational only; never gates enrollment.
const OSA_DIAGNOSED_OPTIONS: Option<SleepioOsaDiagnosed>[] = [
  {label: 'Yes', value: 'yes'},
  {label: 'No', value: 'no'},
  {label: "I'm not sure", value: 'not_sure'},
];

const OSA_TREATMENT_OPTIONS: Option<SleepioOsaTreatment>[] = [
  {label: 'CPAP machine', value: 'cpap'},
  {label: 'GLP-1 medication', value: 'glp1'},
  {label: 'Oral appliance', value: 'oral_appliance'},
  {label: 'Not currently being treated', value: 'not_treated'},
];

// Consent v2.0 — mirrors nyx PR #3796 (RET-2694). The ToS URL points at the
// amended Rise Health ToS / Telehealth Informed Consent / Privacy doc
// (RET-2693) — must be live before this ships.
const RISE_HEALTH_TOS_URL = 'https://www.risescience.com/rise-health-tos-privacy';

// "Read full consent" accordion — parked while we try a different treatment.
// const SLEEPIO_BILLING_URL = 'https://sleepiorx.com/billing';
//
// "What happens when I submit" disclosure. On nyx this lives in the FAQ; the
// web indication screen has no FAQ, so it lived inside the consent accordion.
// const DISCLOSURE_BODY =
//   "You're submitting your health information to our affiliated medical group, whose licensed clinicians " +
//   "will review it — there's no appointment or video call. Based on that review, a clinician may make a " +
//   "diagnosis and order follow-up care, including the SleepioRx digital therapeutic, or may determine it " +
//   "isn't the right fit and suggest other options. If care is provided and you're eligible, the medical " +
//   'group will bill your insurance — most patients pay $0*, though standard cost share (copay, deductible, ' +
//   'or coinsurance) may apply depending on your plan. Your responses are shared with the medical group and ' +
//   'its care partners only to provide your care, and you can withdraw your consent at any time without ' +
//   'affecting your Rise membership.';

const hasFlaggedDiagnosis = (diagnoses: SleepioPriorDiagnosis[]) =>
  diagnoses.some(value => FLAGGED_DIAGNOSES.includes(value));

const readSleepioData = () => surveyInstance.getValue('sleepioRx') ?? {};

const persistSleepioData = (patch: Record<string, unknown>) => {
  const next = {...readSleepioData(), ...patch};
  surveyInstance.setValue('sleepioRx', next);
  SurveyData.save(surveyInstance);
};

function toggleMulti<T extends string>(current: T[], value: T, exclusiveValues: T[] = ['none' as T]): T[] {
  if (exclusiveValues.includes(value)) {
    return current.length === 1 && current[0] === value ? [] : [value];
  }
  const withoutExclusive = current.filter(item => !exclusiveValues.includes(item));
  return withoutExclusive.includes(value)
    ? withoutExclusive.filter(item => item !== value)
    : [...withoutExclusive, value];
}

// Emit the human-readable LABELS the clinician reads in the charting note, not the
// value slugs — nothing parses the values anymore. Drops "None" and the "Other …"
// placeholder (the free-text entry covers it).
function labelsFor<T extends string>(options: Option<T>[], values: T[], otherValue: T): string[] {
  return values
    .filter(value => value !== 'none' && value !== otherValue)
    .map(value => options.find(option => option.value === value)?.label ?? value);
}

type OptionListProps<T extends string> = {
  options: Option<T>[];
  value?: T;
  values?: T[];
  onSelect: (value: T) => void;
};

// Split a label like "Diphenhydramine (Benadryl, ZzzQuil, etc.)" into a bold
// primary line + a lighter secondary line (the trailing parenthetical), so long
// option labels render on two lines instead of overflowing. Labels without a
// trailing parenthetical render as a single line. Display-only — the full label
// is still what gets emitted on the wire.
const splitOptionLabel = (label: string): {primary: string; secondary?: string} => {
  const match = label.match(/^(.*?)\s(\(.*\))\s*$/);
  return match ? {primary: match[1], secondary: match[2]} : {primary: label};
};

function OptionList<T extends string>({options, value, values, onSelect}: OptionListProps<T>) {
  return (
    <div className={styles.optionList}>
      {options.map(option => {
        const selected = value === option.value || values?.includes(option.value);
        const {primary, secondary} = splitOptionLabel(option.label);
        return (
          <button
            key={option.value}
            type="button"
            className={`${styles.optionButton} ${selected ? styles.optionButtonSelected : ''}`}
            onClick={() => onSelect(option.value)}
          >
            <span className={styles.optionLabelPrimary}>{primary}</span>
            {secondary ? <span className={styles.optionLabelSecondary}>{secondary}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

export const SleepioIntakeInterstitial: React.FC<SleepioIntakeInterstitialProps> = ({initialStep}) => {
  const persisted = readSleepioData();
  const [step, setStep] = useState<IntakeStep>(initialStep ?? persisted.step ?? 'duration');
  const [duration, setDuration] = useState<SleepioSleepDifficultyHistory['duration'] | undefined>(persisted.duration);
  const [frequency, setFrequency] = useState<SleepioSleepDifficultyHistory['frequency'] | undefined>(
    persisted.frequency,
  );
  const [rx, setRx] = useState<string[]>(persisted.rx ?? []);
  const [otc, setOtc] = useState<string[]>(persisted.otc ?? []);
  const [psych, setPsych] = useState<string[]>(persisted.psych ?? []);
  const [otherRx, setOtherRx] = useState(persisted.otherRx ?? '');
  const [otherOtc, setOtherOtc] = useState(persisted.otherOtc ?? '');
  const [otherPsych, setOtherPsych] = useState(persisted.otherPsych ?? '');
  const [concurrent, setConcurrent] = useState<SleepioConcurrentTreatmentOption[]>(persisted.concurrent ?? []);
  const [osaDiagnosed, setOsaDiagnosed] = useState<SleepioOsaDiagnosed | undefined>(persisted.osaDiagnosed);
  const [osaTreatment, setOsaTreatment] = useState<SleepioOsaTreatment | undefined>(persisted.osaTreatment);
  const [priorDiagnoses, setPriorDiagnoses] = useState<SleepioPriorDiagnosis[]>(persisted.priorDiagnoses ?? []);
  const [conditionStable, setConditionStable] = useState<SleepioConditionStability | undefined>(
    persisted.conditionStable,
  );
  const [consent, setConsent] = useState(Boolean(persisted.consent));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardUploaded, setCardUploaded] = useState(Boolean(persisted.cardUploaded));

  // Mailing address (optional post-intake step). Prefill the state from the
  // insurance_state the user already picked (both are 2-letter codes).
  const prefilledState =
    typeof surveyInstance.data?.insurance_state === 'string' ? surveyInstance.data.insurance_state.toUpperCase() : '';
  const [addressLine1, setAddressLine1] = useState<string>(persisted.addressLine1 ?? '');
  const [addressLine2, setAddressLine2] = useState<string>(persisted.addressLine2 ?? '');
  const [addressCity, setAddressCity] = useState<string>(persisted.addressCity ?? '');
  const [addressState, setAddressState] = useState<string>(persisted.addressState ?? prefilledState);
  const [addressZip, setAddressZip] = useState<string>(persisted.addressZip ?? '');

  // "Read full consent" accordion — parked while we try a different treatment.
  // const [consentOpen, setConsentOpen] = useState(false);
  // const toggleConsentOpen = () => {
  //   setConsentOpen(prev => {
  //     const next = !prev;
  //     if (next) {
  //       Analytics.track('SleepioRx Indication Consent Expanded');
  //     }
  //     return next;
  //   });
  // };

  // One low-cardinality view event for every intake step (step is the property,
  // not the event name) — e.g. indication-screen → Intake Submitted CVR.
  // Fires on mount too, so a refresh-restore counts as a view of the restored step.
  useEffect(() => {
    Analytics.track('SleepioRx Step Viewed', {step});
  }, [step]);

  const goToStep = (nextStep: IntakeStep, patch: Record<string, unknown> = {}) => {
    persistSleepioData({...patch, step: nextStep});
    setError(null);
    setStep(nextStep);
  };

  const buildMedications = (): SleepioMedications => ({
    prescription: labelsFor(RX_OPTIONS, rx, 'other_rx'),
    otc: labelsFor(OTC_OPTIONS, otc, 'other_otc'),
    psychiatric: labelsFor(PSYCH_MED_OPTIONS, psych, 'other_psych'),
    ...(rx.includes('other_rx') && otherRx.trim() ? {otherPrescription: otherRx.trim()} : {}),
    ...(otc.includes('other_otc') && otherOtc.trim() ? {otherOtc: otherOtc.trim()} : {}),
    ...(psych.includes('other_psych') && otherPsych.trim() ? {otherPsychiatric: otherPsych.trim()} : {}),
  });

  // Web sends ONLY the new MH fields. The backend keys off `priorDiagnoses !== undefined`
  // to take the informational path; it never sets mhRedFlag here — the provider decides.
  const buildMentalHealth = (): SleepioMentalHealthScreen => ({
    priorDiagnoses,
    ...(conditionStable ? {conditionStable} : {}),
  });

  const submitIntake = async () => {
    if (!duration || !frequency || !concurrent.length || !priorDiagnoses.length) {
      setError('Some information is missing. Please go back and complete the previous steps.');
      return;
    }
    if (hasFlaggedDiagnosis(priorDiagnoses) && !conditionStable) {
      setError('Some information is missing. Please go back and complete the previous steps.');
      return;
    }
    if (!consent) {
      setError('Please acknowledge the clinical review before continuing.');
      return;
    }

    const surveyData = SurveyData.read() ?? surveyInstance.data;
    try {
      assertAdultDob(surveyData.dob ?? '');
      const personId = Number(surveyData.person_id);
      const token = surveyData.eligibility_token;
      if (!personId || !token) {
        throw new Error('Missing account information. Please go back and re-enter your contact details.');
      }

      const consentPayload = {
        acknowledged: true,
        version: SLEEPIO_CONSENT_VERSION,
        at: new Date().toISOString(),
        // Provenance: prove exactly which disclosure text + legal doc the user saw.
        disclosureVersion: SLEEPIO_DISCLOSURE_VERSION,
        docVersion: SLEEPIO_CONSENT_DOC_VERSION,
      };

      const request: NewAsyncPatientRequest = {
        personId,
        firstName: surveyData.first_name,
        lastName: surveyData.last_name,
        email: surveyData.email,
        phone: `+1${normalizePhone(surveyData.phone ?? '')}`,
        dateOfBirth: convertDateToISODate(surveyData.dob),
        gender: typeof surveyData.gender === 'string' ? surveyData.gender.toLowerCase() : undefined,
        state: String(surveyData.insurance_state ?? '').toLowerCase(),
        memberId: surveyData.member_id,
        // Prefer the payer Sohar discovery resolved (its key shares our namespace for
        // common payers but covers edge ones the picker can't); else the user's pick.
        payerName: surveyData.discovered_payer_name ?? getPayerIdByValue(surveyData.insurance_coverage),
        sleepDifficultyHistory: {duration, frequency},
        medications: buildMedications(),
        concurrentTreatment: concurrent,
        mentalHealthScreen: buildMentalHealth(),
        asyncRxConsent: consentPayload,
        isi: buildIsiSnapshot(surveyData),
        osaRisk: buildOsaRisk(surveyData, {diagnosed: osaDiagnosed, treatment: osaTreatment}),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        metadata: {
          surface: 'web',
          userAgent: window.navigator.userAgent,
          version: process.env.REACT_APP_VERSION ?? '1.125.0',
          referralCode: surveyData.referral_code,
        },
      };

      setSubmitting(true);
      setError(null);
      Analytics.track('SleepioRx Intake Submitted');
      const response = await submitSleepioIntake(request, token);
      persistSleepioData({
        intakeId: response.intakeId,
        mhRedFlag: Boolean(response.mhRedFlag),
        submittedAt: new Date().toISOString(),
      });
      Analytics.track('SleepioRx Intake Created', {
        intake_id: response.intakeId,
        mh_red_flag: response.mhRedFlag,
      });
      // Optional post-intake enrichment steps. Address comes first (zero friction —
      // the user knows it) then the insurance card (higher friction — they may need
      // to go find it), so we capture the easy one before the likely drop-off point.
      goToStep('address');
    } catch (submitError) {
      const message = submitError instanceof Error ? submitError.message : 'Submission failed. Please try again.';
      setError(message);
      Analytics.track('SleepioRx Intake Failed', {error_message: message});
    } finally {
      setSubmitting(false);
    }
  };

  // Optional insurance-card upload, shown after the intake POST succeeds. Reuses the
  // shared uploadInsuranceCardWeb → healthie/upload-insurance-card call (keyed by
  // personId, same endpoint nyx uses). InsuranceCardUpload calls this once per side.
  const handleCardUpload = async (frontFile?: File, backFile?: File) => {
    if (!frontFile && !backFile) {
      return;
    }
    const surveyData = SurveyData.read() ?? surveyInstance.data;
    const personId = Number(surveyData.person_id);
    const token = surveyData.eligibility_token;
    if (!personId || !token) {
      throw new Error('Missing account information. Please try again.');
    }
    try {
      await uploadInsuranceCardWeb(personId, frontFile, backFile, token);
      setCardUploaded(true);
      persistSleepioData({cardUploaded: true});
      if (frontFile) {
        Analytics.track('SleepioRx Insurance Card Upload Success', {side: 'front'});
      }
      if (backFile) {
        Analytics.track('SleepioRx Insurance Card Upload Success', {side: 'back'});
      }
    } catch (uploadError) {
      const message = uploadError instanceof Error ? uploadError.message : 'Upload failed. Please try again.';
      Analytics.track('SleepioRx Insurance Card Upload Failed', {error_message: message});
      throw uploadError; // let InsuranceCardUpload surface the per-card error
    }
  };

  const advanceFromCard = () => {
    if (!cardUploaded) {
      Analytics.track('SleepioRx Insurance Card Skipped');
    }
    goToStep('confirmation');
  };

  // Optional mailing address, shown right after intake (before the card step).
  // All-blank = silent skip; partial = ask them to finish; complete = POST
  // healthie/update-address (which must run after intake, so the Patient exists).
  // Optionality isn't signaled.
  const advanceFromAddress = async () => {
    const line1 = addressLine1.trim();
    const city = addressCity.trim();
    const state = addressState.trim();
    const zip = addressZip.trim();
    // `state` is prefilled from insurance_state, so it doesn't count as the user
    // starting an address — otherwise an untouched form could never skip.
    const startedAddress = Boolean(line1 || city || zip || addressLine2.trim());

    if (!startedAddress) {
      Analytics.track('SleepioRx Address Skipped');
      goToStep('insuranceCard');
      return;
    }
    if (!line1 || !city || !state || !zip) {
      setError('Please complete your street, city, state, and ZIP — or leave them all blank to continue.');
      return;
    }

    const surveyData = SurveyData.read() ?? surveyInstance.data;
    const personId = Number(surveyData.person_id);
    const token = surveyData.eligibility_token;
    if (!personId || !token) {
      setError('Missing account information. Please try again.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await updatePatientAddressWeb(
        personId,
        {addressLine1: line1, addressLine2: addressLine2.trim() || undefined, city, state, zip},
        token,
      );
      persistSleepioData({addressSaved: true});
      Analytics.track('SleepioRx Address Submitted');
      goToStep('insuranceCard');
    } catch (addressError) {
      const message = addressError instanceof Error ? addressError.message : 'Could not save your address. Please try again.';
      setError(message);
      Analytics.track('SleepioRx Address Failed', {error_message: message});
    } finally {
      setSubmitting(false);
    }
  };

  const renderFooter = (disabled: boolean, onContinue: () => void, title = 'Next', secondary?: React.ReactNode) => (
    <div className={styles.footer}>
      <ButtonSolid title={title} variant="purple" disabled={disabled} loading={submitting} onClick={onContinue} />
      {secondary}
    </div>
  );

  function renderSingle<T extends string>(
    title: string,
    headline: string,
    options: Option<T>[],
    value: T | undefined,
    onSelect: (value: T) => void,
    onContinue: () => void,
  ) {
    return (
      <div className={styles.container}>
        <div className={styles.kicker}>{title}</div>
        <h1 className={styles.title}>{headline}</h1>
        <OptionList options={options} value={value} onSelect={onSelect} />
        {error ? <p className={styles.error}>{error}</p> : null}
        {renderFooter(!value, onContinue)}
      </div>
    );
  }

  if (step === 'duration') {
    return renderSingle(
      'Sleep history',
      'How long have you been having trouble with your sleep?',
      DURATION_OPTIONS,
      duration,
      value => {
        setDuration(value);
        persistSleepioData({duration: value});
      },
      () => goToStep('frequency'),
    );
  }

  if (step === 'frequency') {
    return renderSingle(
      'Sleep history',
      'On average, how many nights per week do you have trouble falling asleep, staying asleep, or waking too early?',
      FREQUENCY_OPTIONS,
      frequency,
      value => {
        setFrequency(value);
        persistSleepioData({frequency: value});
      },
      () => goToStep('rx'),
    );
  }

  const medicationStep = (
    stepName: 'rx' | 'otc' | 'psych',
    title: string,
    headline: string,
    options: Option<string>[],
    values: string[],
    setValues: (values: string[]) => void,
    otherValue: string,
    otherText: string,
    setOtherText: (value: string) => void,
    next: IntakeStep,
  ) => (
    <div className={styles.container}>
      <div className={styles.kicker}>{title}</div>
      <h1 className={styles.title}>{headline}</h1>
      <OptionList
        options={options}
        values={values}
        onSelect={value => {
          const nextValues = toggleMulti(values, value);
          setValues(nextValues);
          persistSleepioData({[stepName]: nextValues});
        }}
      />
      {values.includes(otherValue) ? (
        <input
          className={styles.textInput}
          value={otherText}
          onChange={event => {
            setOtherText(event.target.value);
            persistSleepioData({[`other${stepName[0].toUpperCase()}${stepName.slice(1)}`]: event.target.value});
          }}
          placeholder="Which medication?"
        />
      ) : null}
      {renderFooter(values.length === 0, () => goToStep(next))}
    </div>
  );

  if (step === 'rx') {
    return medicationStep(
      'rx',
      'Medications',
      'Are you taking any prescription sleep medications?',
      RX_OPTIONS,
      rx,
      setRx,
      'other_rx',
      otherRx,
      setOtherRx,
      'otc',
    );
  }

  if (step === 'otc') {
    return medicationStep(
      'otc',
      'Medications',
      'Any over-the-counter sleep aids, 3+ nights per week?',
      OTC_OPTIONS,
      otc,
      setOtc,
      'other_otc',
      otherOtc,
      setOtherOtc,
      'psych',
    );
  }

  if (step === 'psych') {
    return medicationStep(
      'psych',
      'Medications',
      'Are you currently prescribed or taking any of these medications for a mental health condition?',
      PSYCH_MED_OPTIONS,
      psych,
      setPsych,
      'other_psych',
      otherPsych,
      setOtherPsych,
      'concurrent',
    );
  }

  if (step === 'concurrent') {
    return (
      <div className={styles.container}>
        <div className={styles.kicker}>Clinical intake</div>
        <h1 className={styles.title}>Are you currently seeing a mental-health provider?</h1>
        <OptionList
          options={CONCURRENT_OPTIONS}
          values={concurrent}
          onSelect={value => {
            const next = toggleMulti(concurrent, value, ['neither', 'prefer_not_to_say']);
            setConcurrent(next);
            persistSleepioData({concurrent: next});
          }}
        />
        {renderFooter(concurrent.length === 0, () => {
          // High-OSA-risk users get the 2 informational OSA follow-up questions
          // before prior diagnosis; everyone else skips straight to it.
          const surveyData = SurveyData.read() ?? surveyInstance.data;
          goToStep(getIsHighOsaRisk(surveyData) ? 'osaDiagnosed' : 'priorDiagnosis');
        })}
      </div>
    );
  }

  // OSA follow-up (high-risk only) — informational, never gates. nyx RET-2598.
  if (step === 'osaDiagnosed') {
    return renderSingle(
      'Sleep apnea',
      'Have you ever been diagnosed with sleep apnea?',
      OSA_DIAGNOSED_OPTIONS,
      osaDiagnosed,
      value => {
        setOsaDiagnosed(value);
        persistSleepioData({osaDiagnosed: value});
      },
      () => goToStep('osaTreatment'),
    );
  }

  if (step === 'osaTreatment') {
    return renderSingle(
      'Sleep apnea',
      'Are you currently being treated for sleep apnea?',
      OSA_TREATMENT_OPTIONS,
      osaTreatment,
      value => {
        setOsaTreatment(value);
        persistSleepioData({osaTreatment: value});
      },
      () => {
        Analytics.track('SleepioRx OSA Follow-up Answered', {
          diagnosed: osaDiagnosed,
          treatment: osaTreatment,
        });
        goToStep('priorDiagnosis');
      },
    );
  }

  // NEW 2026-06 — prior-diagnosis screen (replaces psychosis/mania/seizure stems).
  if (step === 'priorDiagnosis') {
    return (
      <div className={styles.container}>
        <div className={styles.kicker}>Medical history</div>
        <h1 className={styles.title}>Have you ever been diagnosed with any of the following?</h1>
        <OptionList
          options={PRIOR_DIAGNOSIS_OPTIONS}
          values={priorDiagnoses}
          onSelect={value => {
            const next = toggleMulti(priorDiagnoses, value, PRIOR_DIAGNOSIS_EXCLUSIVE);
            setPriorDiagnoses(next);
            persistSleepioData({priorDiagnoses: next});
          }}
        />
        {renderFooter(priorDiagnoses.length === 0, () => {
          // Only ask the stability follow-up when a flagged condition is selected.
          if (hasFlaggedDiagnosis(priorDiagnoses)) {
            goToStep('conditionStability');
          } else {
            // Clear any stale stability answer when no flagged dx remains.
            setConditionStable(undefined);
            goToStep('indication', {conditionStable: undefined});
          }
        })}
      </div>
    );
  }

  // NEW 2026-06 — conditional stability follow-up (only when a flagged dx is selected).
  if (step === 'conditionStability') {
    return renderSingle(
      'Medical history',
      'Is your condition stable and well managed with treatment (for example, taking medication for it)?',
      CONDITION_STABILITY_OPTIONS,
      conditionStable,
      value => {
        setConditionStable(value);
        persistSleepioData({conditionStable: value});
      },
      () => goToStep('indication'),
    );
  }

  if (step === 'indication') {
    const fitReasons = [
      {
        title: 'Reviewed by a licensed clinician',
        body: 'based on your responses. No live visit required.',
      },
      {
        title: 'FDA-cleared digital CBT-I',
        body: 'The gold-standard insomnia treatment, delivered through a structured app-based program.',
      },
      {
        title: 'Most people see real improvement',
        body: 'Within 6 weeks of short daily sessions.',
      },
    ];

    return (
      <div className={`${styles.container} ${styles.fitContainer}`}>
        <div className={styles.kicker}>Rise + SleepioRx</div>
        <h1 className={`${styles.title} ${styles.fitTitle}`}>You're a fit for SleepioRx.</h1>
        <p className={`${styles.body} ${styles.fitBody}`}>
          Based on your responses, you may qualify for SleepioRx, an FDA-cleared digital CBT-I program covered by your
          insurance.
        </p>
        <div className={`${styles.panel} ${styles.fitPanel}`}>
          <ul className={styles.fitReasonList}>
            {fitReasons.map(reason => (
              <li className={styles.fitReason} key={reason.title}>
                <span className={styles.fitCheck} aria-hidden="true" />
                <span>
                  <strong>{reason.title}</strong>
                  <span className={styles.fitReasonBody}> {reason.body}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.consentPanel}>
          <p className={styles.consentTitle}>Submit your intake for clinical review</p>
          <p className={styles.consentBody}>
            A licensed clinician will review your responses asynchronously. Depending on your symptoms, they might
            recommend to start CBT-I via SleepioRx. You will receive more info via text and email with details.
          </p>
          <label className={styles.consentLabel}>
            <input
              className={styles.consentCheckbox}
              type="checkbox"
              checked={consent}
              onChange={event => {
                setConsent(event.target.checked);
                persistSleepioData({consent: event.target.checked});
              }}
            />
            <span>I consent to an asynchronous clinical review of my responses by a licensed clinician.</span>
          </label>
          {/* "Read full consent" accordion (nyx PR #3796 treatment) — parked while
              we try a different treatment.
          <button
            type="button"
            className={styles.consentToggle}
            aria-expanded={consentOpen}
            onClick={toggleConsentOpen}
          >
            {consentOpen ? 'Hide details' : 'Read full consent'}
          </button>
          {consentOpen ? (
            <div className={styles.consentDetail}>
              <p className={styles.consentDetailText}>{DISCLOSURE_BODY}</p>
              <p className={styles.consentDetailText}>
                This includes the{' '}
                <a
                  href={RISE_HEALTH_TOS_URL}
                  className={styles.consentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Rise Health Terms of Service and the Telehealth Informed Consent &amp; Privacy Policy
                </a>
                . You can withdraw your consent at any time without affecting your Rise membership.
              </p>
            </div>
          ) : null}
          */}
          <p className={styles.termsText}>
            By continuing, I confirm that I have read and agree to the{' '}
            <a href={RISE_HEALTH_TOS_URL} className={styles.consentLink} target="_blank" rel="noopener noreferrer">
              Terms, Consents, and Conditions
            </a>{' '}
            including the financial responsibility and billing terms. .
          </p>
        </div>
        <p className={styles.finePrint}>
          Note that an insurance Explanation of Benefits is not a bill. Most patients access SleepioRx for $0 via the
          SleepioRx Copay Program, which we'll help enroll automatically.
        </p>
        {error ? <p className={styles.error}>{error}</p> : null}
        {renderFooter(
          !consent || submitting,
          submitIntake,
          'Submit',
          <p className={styles.trustLine}>🕑 Typically reviewed within 24 hours. Weekends may be slower</p>,
        )}
      </div>
    );
  }

  // Optional insurance-card step, shown after the address step. Intake is already
  // submitted; the card is a separate upload that only enriches the existing record.
  if (step === 'insuranceCard') {
    return (
      <div className={styles.container}>
        <div className={styles.kicker}>Insurance</div>
        <h1 className={styles.title}>Add your insurance card</h1>
        <p className={styles.body}>Upload both sides of your insurance card so our team can verify your benefits.</p>
        <InsuranceCardUpload onUpload={handleCardUpload} />
        {renderFooter(false, advanceFromCard, 'Continue')}
      </div>
    );
  }

  // Optional mailing address (CMS-1500 Box 5), shown right after intake (before the
  // card). Posts to healthie/update-address only when complete; all-blank silently
  // advances to the card step.
  if (step === 'address') {
    return (
      <div className={styles.container}>
        <div className={styles.kicker}>Address</div>
        <h1 className={styles.title}>What's your address?</h1>
        <p className={styles.body}>We use your address to submit your insurance information.</p>
        <input
          className={styles.textInput}
          value={addressLine1}
          onChange={event => {
            setAddressLine1(event.target.value);
            persistSleepioData({addressLine1: event.target.value});
          }}
          placeholder="Street address"
          autoComplete="address-line1"
        />
        <input
          className={styles.textInput}
          value={addressLine2}
          onChange={event => {
            setAddressLine2(event.target.value);
            persistSleepioData({addressLine2: event.target.value});
          }}
          placeholder="Apt, suite, unit (optional)"
          autoComplete="address-line2"
        />
        <input
          className={styles.textInput}
          value={addressCity}
          onChange={event => {
            setAddressCity(event.target.value);
            persistSleepioData({addressCity: event.target.value});
          }}
          placeholder="City"
          autoComplete="address-level2"
        />
        <select
          className={`${styles.textInput} ${styles.selectInput}`}
          value={addressState}
          onChange={event => {
            setAddressState(event.target.value);
            persistSleepioData({addressState: event.target.value});
          }}
          autoComplete="address-level1"
        >
          <option value="">State</option>
          {US_STATES.map(({text, value}) => (
            <option key={value} value={value}>
              {text}
            </option>
          ))}
        </select>
        <input
          className={styles.textInput}
          value={addressZip}
          onChange={event => {
            setAddressZip(event.target.value);
            persistSleepioData({addressZip: event.target.value});
          }}
          placeholder="ZIP code"
          inputMode="numeric"
          autoComplete="postal-code"
          maxLength={10}
        />
        {error ? <p className={styles.error}>{error}</p> : null}
        {renderFooter(submitting, advanceFromAddress, 'Continue')}
      </div>
    );
  }

  if (step === 'confirmation' || step === 'final') {
    const dayOfWeek = new Date().getDay();
    const showWeekendReviewNote = dayOfWeek === 0 || dayOfWeek === 5 || dayOfWeek === 6;
    const reviewCopy = `A licensed psychologist will review your intake within the next day or so.${
      showWeekendReviewNote ? ' It might take a little longer over the weekend.' : ''
    }`;

    return (
      <div className={styles.container}>
        <div className={styles.kicker}>You're in</div>
        <h1 className={styles.title}>We're working on it.</h1>
        <p className={styles.body}>A licensed clinician from our care team will review your intake.</p>
        <div className={styles.timeline}>
          {[
            ['Review', reviewCopy],
            [
              'Approval',
              "If you're a good fit, they'll approve you for SleepioRx. You'll get an SMS and email from SleepioRx with sign-up details.",
            ],
            [
              'Get started',
              'Follow the instructions to download the SleepioRx app, enter your access code, and start your program.',
            ],
          ].map(([title, body], index) => (
            <div className={styles.timelineStep} key={title}>
              <div className={styles.stepNumber}>{index + 1}</div>
              <div>
                <p className={styles.stepTitle}>{title}</p>
                <p className={styles.stepBody}>{body}</p>
              </div>
            </div>
          ))}
        </div>
        <div className={styles.headsUp}>
          <p className={styles.headsUpTitle}>Heads up</p>
          <p className={styles.headsUpBody}>
            The message will come from SleepioRx with activation instructions, usually after the clinician review is
            complete.
          </p>
        </div>
      </div>
    );
  }

  return null;
};

export default SleepioIntakeInterstitial;
